/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Drawer,
	Stack,
	IconButton,
	Typography,
	FormHelperText,
	TextField,
	FormControl,
	FormLabel,
	styled,
	Button,
	CircularProgress,
	Badge,
	Tooltip,
	Grid,
	Collapse,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import { InstantDealSelect } from '@/components/selects/DealSelect';
import EmailTemplateSelect from '@/components/selects/EmailTemplateSelect';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';
import { setDialogData } from '@/features/dialog/dialogSlice';
import { useGetLatestDealSOAsQuery } from '@/features/factFind/factFindApi';
import { UserEmailSelect } from '@/components/selects/UserEmailSelect';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFolioDrawerContext } from '@/components/Drawers/FolioDrawer/FolioDrawerContext';

const HiddenHeader = ({ includeAlias = false }) => {
	const { values, touched, errors } = useFormikContext();

	return (
		<Stack>
			<Typography sx={{ fontSize: '12px' }}>
				<span
					style={{
						...(touched.id && Boolean(errors.id) && { color: 'red' }),
					}}
				>
					<span style={{ fontWeight: 'bold' }}>{'Client: '}</span>
					{values.client?.name ?? '""'}
					{', '}
				</span>
				{values.deal?.dealName && (
					<span>
						<span style={{ fontWeight: 'bold' }}>{'Deal: '}</span>
						{values.deal.dealName}
						{', '}
					</span>
				)}
				{includeAlias && (
					<span>
						<span style={{ fontWeight: 'bold' }}>{'From: '}</span>
						{values.from?.name ?? JSON.stringify(values.from)}
						{', '}
					</span>
				)}
				<span
					style={{
						...(touched.recipient && Boolean(errors.recipient) && { color: 'red' }),
					}}
				>
					<span style={{ fontWeight: 'bold' }}>{'To: '}</span>
					{values.recipient.length > 0
						? values.recipient.map((c) => c.contactEmail).join(',')
						: '""'}
					{', '}
				</span>
				{values.cc.length > 0 && (
					<span>
						<span style={{ fontWeight: 'bold' }}>{'CC: '}</span>
						{values.cc.map((c) => c.contactEmail).join(',')}
						{', '}
					</span>
				)}
				{values.bcc.length > 0 && (
					<span>
						<span style={{ fontWeight: 'bold' }}>{'BCC: '}</span>
						{values.bcc.map((c) => c.contactEmail).join(',')}
						{', '}
					</span>
				)}
				<span
					style={{
						...(touched.subject && Boolean(errors.subject) && { color: 'red' }),
					}}
				>
					<span style={{ fontWeight: 'bold' }}>{'Subject: '}</span>
					{`"${values.subject ?? ''}"`}
				</span>
			</Typography>
		</Stack>
	);
};
const ContactSelection = ({ value, error }) => {
	const { values, setFieldValue, isSubmitting } = useFormikContext();

	return (
		<InstantClientSelect
			// clientId={formik.values.client?.id}
			// clientName={formik.values[CLIENT_NAME]}
			disabled={isSubmitting}
			showContact={true}
			attendees={values[value]}
			setClient={(contacts) => {
				if (contacts) {
					console.log(`Here is the selected contact for ${value}:`, contacts);
					setFieldValue(value, contacts);
				}
			}}
			error={error}
		/>
	);
};

const EmailFormHeader = ({ showCC, setShowCC, showBCC, setShowBCC, setLoadingTemplate }) => {
	const params = useParams();
	const { isMaximised } = useFolioDrawerContext();
	const { getFieldProps, values, touched, errors, setFieldValue, isSubmitting } =
		useFormikContext();
	const dispatch = useDispatch();

	const [hideHeader, setHideHeader] = useState(false);

	const data = useSelector((state) => state.dialog);
	const preloadData = useSelector((state) => state.dialog?.data?.email);

	const { data: soas } = useGetLatestDealSOAsQuery(
		{ dealId: values.deal?.dealId },
		{ skip: !values.deal?.dealId },
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const sendEmailAsAlias = Boolean(getFeatureFlags('sendEmailAsAlias')?.active);

	const hasValidSoa = soas?.[0]?.downloadUrl != null;

	const templateRequirements = {
		client: values.client,
		deal: values.deal,
		statementOfAdvice: hasValidSoa,
		// user: true,
		// brokerage: true,
	};

	return (
		<Stack>
			<Stack
				direction='row'
				justifyContent={hideHeader ? 'space-between' : 'right'}
				alignItems='center'
			>
				{hideHeader && <HiddenHeader includeAlias={sendEmailAsAlias} />}
				<Box>
					<Button
						variant='text'
						size='small'
						onClick={(e) => setHideHeader((prev) => !prev)}
					>
						{hideHeader ? 'SHOW' : 'HIDE'}
					</Button>
				</Box>
			</Stack>

			<TransitionGroup>
				{!hideHeader && (
					<Collapse>
						<Grid container spacing={1} alignContent={'center'}>
							<Grid size={isMaximised ? 6 : 12}>
								<FormControl
									fullWidth
									required
									{...getFieldProps('id')}
									error={touched.id && Boolean(errors.id)}
									sx={{ width: '100%' }}
								>
									<FormLabel>{'Client'}</FormLabel>
									<InstantClientSelect
										clientId={values.client?.clientId}
										clientName={values.client?.name}
										setClient={(client) => {
											setFieldValue('recipient', []);
											setTimeout(() => {
												setFieldValue('client', client);
												setFieldValue('id', client?.clientId);
												setFieldValue('deal', null);
											}, 0);
										}}
										disabled={
											isSubmitting ||
											Boolean(params?.clientId) ||
											Boolean(params?.dealId)
										}
										error={touched.id && Boolean(errors.id)}
										width={'100%'}
									/>
									{touched.id && Boolean(errors.id) && (
										<FormHelperText error>{errors.id}</FormHelperText>
									)}
								</FormControl>
							</Grid>
							<Grid size={isMaximised ? 6 : 12}>
								<FormControl fullWidth sx={{ width: '100%' }}>
									<FormLabel>{'Associated deal or claim'}</FormLabel>
									<InstantDealSelect
										disabled={
											isSubmitting ||
											!values.client ||
											Boolean(params?.dealId)
										}
										clientId={values.client?.clientId}
										dealId={values.deal?.dealId}
										setDeal={(newDeal) => {
											console.log('newDeal', newDeal);
											setFieldValue('deal', newDeal);
											setFieldValue(
												'id',
												newDeal?.dealId ??
													values.client?.clientId ??
													values.client?.hubspotId,
											);
										}}
										autoPopulate={true}
									/>
								</FormControl>
							</Grid>
							{sendEmailAsAlias && (
								<Grid size={isMaximised ? 6 : 12}>
									<FormControl required fullWidth sx={{ width: '100%' }}>
										<Box sx={{ minHeight: '24.25px' }}>
											<FormLabel>From</FormLabel>
										</Box>
										<UserEmailSelect
											value={values.from ?? null}
											disabled={isSubmitting}
											delegatedEmails={values.delegatedEmails}
											onChange={(option) => setFieldValue('from', option)}
										/>
									</FormControl>
								</Grid>
							)}

							<Grid size={isMaximised && sendEmailAsAlias ? 6 : 12}>
								<FormControl
									fullWidth
									sx={{ width: '100%' }}
									required
									error={touched.recipient && Boolean(errors.recipient)}
								>
									<Stack
										direction='row'
										justifyContent={'space-between'}
										alignItems={'center'}
									>
										<FormLabel>{'To'}</FormLabel>
										<Stack direction='row'>
											{!showCC && (
												<Tooltip
													title={
														values.cc.length > 0
															? `${values.cc
																	.map((c) => c.contactEmail)
																	.join(', ')}`
															: ''
													}
												>
													<Badge
														badgeContent={values.cc.length}
														color='primary'
													>
														<Button
															variant='text'
															size='small'
															sx={{
																textTransform: 'none',
															}}
															onClick={() => setShowCC(true)}
														>
															Cc
														</Button>
													</Badge>
												</Tooltip>
											)}
											{!showBCC && (
												<Tooltip
													title={
														values.bcc.length > 0
															? `${values.bcc
																	.map((c) => c.contactEmail)
																	.join(', ')}`
															: ''
													}
												>
													<Badge
														badgeContent={values.bcc.length}
														color='secondary'
													>
														<Button
															variant='text'
															size='small'
															sx={{
																textTransform: 'none',
															}}
															onClick={() => setShowBCC(true)}
														>
															Bcc
														</Button>
													</Badge>
												</Tooltip>
											)}
											{showBCC && showCC && (
												<Button
													variant='text'
													size='small'
													sx={{
														textTransform: 'none',
													}}
													onClick={() => {
														setShowBCC(false);
														setShowCC(false);
													}}
												>
													Hide
												</Button>
											)}
										</Stack>
									</Stack>
									<ContactSelection
										value={'recipient'}
										error={touched.recipient && Boolean(errors.recipient)}
									/>
									{touched.recipient && Boolean(errors.recipient) && (
										<FormHelperText error>
											{touched.recipient && errors.recipient}
										</FormHelperText>
									)}
								</FormControl>
							</Grid>
							{showCC && (
								<Grid size={isMaximised ? 6 : 12}>
									<FormControl fullWidth sx={{ width: '100%' }}>
										<FormLabel>{'Cc'}</FormLabel>

										<ContactSelection value={'cc'} />
									</FormControl>
								</Grid>
							)}
							{showBCC && (
								<Grid size={isMaximised ? 6 : 12}>
									<FormControl fullWidth sx={{ width: '100%' }}>
										<FormLabel>{'Bcc'}</FormLabel>
										<ContactSelection value={'bcc'} />
									</FormControl>
								</Grid>
							)}
							<Grid size={isMaximised ? 8 : 12}>
								<FormControl
									required
									error={touched.subject && Boolean(errors.subject)}
									fullWidth
									sx={{ width: '100%' }}
								>
									<FormLabel>{'Subject'}</FormLabel>
									<TextField
										fullWidth={true}
										name='subject'
										placeholder='Subject'
										{...getFieldProps('subject')}
										error={touched.subject && Boolean(errors.subject)}
										helperText={touched.subject && errors.subject}
										disabled={isSubmitting}
									/>
								</FormControl>
							</Grid>
							<Grid size={isMaximised ? 4 : 12}>
								<FormControl fullWidth sx={{ width: '100%' }}>
									<FormLabel>{'Template'}</FormLabel>
									{/* <Tooltip
						title={
							!values.deal
								? 'Please select a deal first in order to view templates'
								: ''
						}
						placement='bottom-start'
					>
						<div> */}
									<EmailTemplateSelect
										value={values.template}
										setValue={(template) => {
											setFieldValue('template', template);
											if (!template) {
												setFieldValue('html', '');
												setFieldValue('subject', '');
												// dispatch(setDialogData({ dialogName: 'email', data: {} }));
											}
											if (template?.id) {
												setLoadingTemplate(true);
											}
											dispatch(
												setDialogData({ dialogName: 'email', data: {} }),
											);
										}}
										dealId={values.deal?.dealId}
										requirements={templateRequirements}
										type={preloadData?.template}
										// disabled={!values.deal || isSubmitting}
									/>
									{/* </div>
					</Tooltip> */}
								</FormControl>
							</Grid>
						</Grid>
					</Collapse>
				)}
			</TransitionGroup>
		</Stack>
	);
};

export default EmailFormHeader;
